//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import { ApiModel, ApiArrayModel } from 'src/app/common/model';
import ImageDetailsModel from './images-details.model';

/**
 * @service ImageDetailsService
 * @description Service for managing image-related operations within the application.
 * This service handles the retrieval, upload, download, and deletion of images associated with
 * farm sites, ponds, and cultures. It interacts with the backend API to manage image metadata
 * and binary data, often interfacing with S3 or local storage via the backend.
 * Extends the base Service class with generic type ImageDetailsModel.
 *
 * @feature 1. Retrieve image details and counts for farm sites
 * @feature 2. Upload and download images (including S3 integration)
 * @feature 3. Delete images and associated pond records
 * @feature 4. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<ImageDetailsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ImageDetailsService extends Service<ImageDetailsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/');
  }

  /**
   * @method getImageDetails
   * @async
   * @description Fetch image details and counts for a specific farm site.
   *
   * Makes an HTTP POST request to retrieve image metadata associated with the provided farm site ID.
   *
   * @param {any} farmSiteId - The unique identifier of the farm site
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to an array of ImageDetailsModel objects
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   *
   * @example
   * const images = await imageDetailsService.getImageDetails('site-123');
   */
  public getImageDetails(farmSiteId: any) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}imagecount`, {
        'farm_site_id': farmSiteId
      }, this.httpOptions).subscribe(response => {
        resolve(response.image_data);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method downloadImage
   * @async
   * @description Download an image using a specific controller endpoint.
   *
   * @param {any} imageData - Object containing image identification data
   * @param {string} controllerName - The name of the controller to target for the download
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the downloaded image data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public downloadImage(imageData: any, controllerName: string) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}${controllerName}/downloadWeb`, imageData, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method imageDownload
   * @async
   * @description Download an image from the image server (e.g., S3).
   *
   * @param {ImageDetailsModel} imageData - Model containing details of the image to download
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the image data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public imageDownload(imageData: ImageDetailsModel) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.imagebaseUrl}/image/download`, imageData, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method deleteImage
   * @async
   * @description Delete a specific image record.
   *
   * @param {ImageDetailsModel} imageData - Model containing details of the image to delete
   * @returns {Promise<ImageDetailsModel>} Promise resolving to the deleted image data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public deleteImage(imageData: ImageDetailsModel) {
    return new Promise<ImageDetailsModel>((resolve, reject) => {
      const request = this.httpClient.post<ApiModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}imagedelete/`, imageData, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method deletePond
   * @async
   * @description Delete pond records, potentially associated with images.
   *
   * @param {Array<string>} pondId - Array of pond IDs to delete
   * @param {string} pondDelete - Flag or identifier indicating delete action scope
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the operation result
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public deletePond(pondId: Array<string>, pondDelete: string) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}ponddelete/deletePond`, {
        pond_id: pondId, pond_delete: '' + pondDelete
      }, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPonds
   * @async
   * @description Retrieve record counts or details for specific ponds.
   *
   * @param {Array<string>} pondId - Array of pond IDs to query
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the pond data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public getPonds(pondId: Array<string>) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}records/recordsCount`, {
        pond_id: pondId
      }, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method imageRetrieval
   * @async
   * @description Retrieve images from S3 for a specific pond and culture.
   *
   * @param {string} pondId - The unique identifier of the pond
   * @param {string} cultureId - The unique identifier of the pond culture
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to an array of retrieved images
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public imageRetrieval(pondId: string, cultureId: string) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.imagebaseUrl}/image/retrieval_S3`, {
        "pond_id": '' + pondId,
        "pond_culture_id": '' + cultureId,
        "offset": 0
      }, this.httpOptions).subscribe(response => {
        resolve(response.images);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method imageUpload
   * @async
   * @description Upload new image data to the server.
   *
   * @param {ImageDetailsModel} imageData - Model containing the image data to upload
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the uploaded image details
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public imageUpload(imageData: ImageDetailsModel) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.imagebaseUrl}/image/dataInsert`, imageData, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.images);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }


  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears ongoingRequests array
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
